// Custom geometry uploader. Two states share one drop target:
//   - empty/error → a full dashed drop-zone (the primary, discoverable target)
//   - uploaded    → a compact "Loaded <file>" row with a Replace button, so
//                   the map + stats stay visible without scrolling
// A keyboard-operable button (Choose file / Replace) is always the accessible
// single-pointer alternative to dropping, satisfying WCAG 2.2 Dragging
// Movements (2.5.7); drag-and-drop remains available in both states but is
// never the only way to (re)upload.
import { useRef, useState, useEffect } from 'react';
import {
  Box, Button, Typography, Alert, CircularProgress, Stack,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { useDispatch, useSelector } from 'react-redux';
import { uploadCustomGeometry } from '../store/area-slice.js';

const ACCEPTED = '.zip,.kml,.kmz,.geojson,.json';

const CustomGeometryUploader = () => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const { loading, error, data } = useSelector((s) => s.area.stats);

  // "Uploaded" covers both a freshly-chosen file and a custom area restored from
  // a saved draft (stats present without a local file, e.g. after a reload).
  const restored = !fileName && !!data;
  const uploaded = (!!fileName || restored) && !loading && !error;
  const loadedLabel = fileName || data?.name || 'Saved custom area';

  // Move focus to the Replace button when we collapse to the compact state so
  // keyboard users land on the obvious next control rather than nothing. Only
  // after an explicit upload — a passive draft restore must not steal focus.
  useEffect(() => {
    if (uploaded && fileName) replaceRef.current?.focus();
  }, [uploaded, fileName]);

  const submitFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    dispatch(uploadCustomGeometry(file));
  };

  const onInputChange = (e) => {
    submitFile(e.target.files?.[0]);
    // Reset so selecting the same file again re-triggers change.
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    submitFile(e.dataTransfer.files?.[0]);
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED}
      onChange={onInputChange}
      // Visually hidden but reachable: the buttons forward clicks here.
      style={{ display: 'none' }}
      aria-hidden="true"
      tabIndex={-1}
    />
  );

  const dragHandlers = {
    onDragOver: (e) => { e.preventDefault(); setIsDragging(true); },
    onDragLeave: () => setIsDragging(false),
    onDrop,
  };

  if (uploaded) {
    return (
      <Box {...dragHandlers}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
            p: 1.5,
            border: 1,
            borderColor: isDragging ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: isDragging ? 'action.hover' : 'background.default',
            transition: 'border-color 120ms, background-color 120ms',
          }}
        >
          <DescriptionIcon color="primary" aria-hidden="true" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              {restored ? 'Restored saved area' : 'Loaded boundary file'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              title={loadedLabel}
            >
              {loadedLabel}
            </Typography>
          </Box>
          <Button
            ref={replaceRef}
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
            sx={{ flexShrink: 0 }}
          >
            Replace
          </Button>
        </Stack>
        <Typography
          component="span"
          role="status"
          aria-live="polite"
          sx={{
            position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)',
          }}
        >
          {`Loaded ${fileName}.`}
        </Typography>
        {hiddenInput}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        {...dragHandlers}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          bgcolor: isDragging ? 'action.hover' : 'transparent',
          transition: 'background-color 120ms, border-color 120ms',
        }}
      >
        <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
          <UploadFileIcon color="action" aria-hidden="true" fontSize="large" />
          <Typography variant="body1">
            Drag and drop a boundary file here, or
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={loading}
          >
            Choose file
          </Button>
          {hiddenInput}
          <Typography variant="caption" color="text.secondary">
            Accepted formats: Shapefile (.zip), KML, KMZ, GeoJSON. Max 250 MB.
          </Typography>
        </Stack>
      </Box>

      {loading && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }} aria-live="polite">
          <CircularProgress size={18} />
          <Typography variant="body2">
            Processing
            {fileName ? ` "${fileName}"` : ''}
            …
          </Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CustomGeometryUploader;
